<template>
  <b-modal ref="modal" size="xl" hide-header hide-footer centered scrollable
           body-class="p-0" content-class="uim-content" @hidden="onHidden">
    <div v-if="loading && !insight" class="uim-loading">
      <b-spinner variant="primary"/>
      <div class="mt-75">{{ $t('operations.loading') }}</div>
    </div>

    <div v-else-if="insight" class="uim" :class="{ 'uim--dark': isDarkSkin }">

      <!-- ============ HERO ============ -->
      <div class="uim-hero">
        <div class="uim-hero__medal" v-if="rank">
          <span v-if="rank === 1">🥇</span><span v-else-if="rank === 2">🥈</span>
          <span v-else-if="rank === 3">🥉</span><span v-else>#{{ rank }}</span>
        </div>
        <div class="uim-hero__main">
          <div class="uim-hero__name">
            {{ u.fullName }}
            <span v-for="t in (u.buTypes || [])" :key="t"
                  class="uim-sr" :class="t === 'SALES' ? 'uim-sr--s' : 'uim-sr--r'"
                  v-b-tooltip :title="t === 'SALES' ? $t('userInsights.sales') : $t('userInsights.retention')">
              {{ t === 'SALES' ? 'S' : 'R' }}
            </span>
          </div>
          <div class="uim-hero__sub"><i class="fas fa-id-badge mr-25"></i>{{ $t('userInsights.openUser') }}</div>
          <div class="uim-hero__badges">
            <span class="uim-tag" v-if="u.username">
              <i class="fas fa-at mr-25"></i>{{ u.username }}
            </span>
            <span class="uim-tag" v-if="u.extension">
              <i class="fas fa-phone mr-25"></i>{{ $t('userInsights.extension') }} {{ u.extension }}
            </span>
            <span class="uim-tag" v-if="orgNames">
              <i class="fas fa-building mr-25"></i>{{ orgNames }}
            </span>
            <span class="uim-tag" v-if="u.creationTime">
              <i class="fas fa-cake-candles mr-25"></i>{{ $t('userInsights.memberSince') }} {{ u.creationTime | momentDate }}
            </span>
            <span class="uim-tag" v-if="u.email"><i class="fas fa-envelope mr-25"></i>{{ u.email }}</span>
          </div>
        </div>
        <div class="uim-hero__actions">
          <b-link class="uim-hero__close" @click="$refs.modal.hide()"><feather-icon icon="XIcon" size="20"/></b-link>
        </div>
      </div>

      <div class="uim-body">

        <!-- ============ DATE FILTER (scopes the monetary stats & drills) ============ -->
        <div class="uim-range">
          <calendar-bar v-model="dateRange" @input="onRangeChanged"/>
        </div>

       <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">
        <!-- ============ KPI TILES (click = drill into the transactions) ============ -->
        <div class="uim-kpis">
          <div class="uim-kpi uim-kpi--dep uim-kpi--click"
               @click="drill($t('userInsights.totalBrought'), depositDrillFilters())">
            <div class="uim-kpi__label">{{ $t('userInsights.totalBrought') }}</div>
            <div class="uim-kpi__value">{{ money(m.deposits.total) }}</div>
            <div class="uim-kpi__foot">
              {{ m.deposits.count }} {{ $t('userInsights.deposits') }}
              · {{ $t('userInsights.net') }} {{ money(m.net || 0) }}
            </div>
            <i class="fas fa-expand uim-kpi__expand"></i>
          </div>
          <div class="uim-kpi uim-kpi--ftd uim-kpi--click"
               @click="drill($t('userInsights.ftds'), { ...depositDrillFilters(), isFtd: true })">
            <div class="uim-kpi__label">{{ $t('userInsights.ftds') }}</div>
            <div class="uim-kpi__value">{{ m.ftd.count }}</div>
            <div class="uim-kpi__foot">{{ money(m.ftd.amount) }}</div>
            <i class="fas fa-expand uim-kpi__expand"></i>
          </div>
          <div class="uim-kpi uim-kpi--ret uim-kpi--click"
               @click="drill($t('userInsights.retention'), { ...depositDrillFilters(), isFtd: false })">
            <div class="uim-kpi__label">{{ $t('userInsights.retention') }}</div>
            <div class="uim-kpi__value">{{ money(m.retention.amount) }}</div>
            <div class="uim-kpi__foot">{{ m.retention.count }} {{ $t('userInsights.deposits') }}</div>
            <i class="fas fa-expand uim-kpi__expand"></i>
          </div>
          <div class="uim-kpi uim-kpi--wd uim-kpi--click"
               @click="drill($t('userInsights.withdrawals'), withdrawDrillFilters())">
            <div class="uim-kpi__label">{{ $t('userInsights.withdrawals') }}</div>
            <div class="uim-kpi__value">{{ money(withdrawals.total) }}</div>
            <div class="uim-kpi__foot">{{ withdrawals.count }} {{ $t('userInsights.withdrawals').toLowerCase() }}</div>
            <i class="fas fa-expand uim-kpi__expand"></i>
          </div>
          <div class="uim-kpi uim-kpi--avg uim-kpi--click"
               @click="drill($t('userInsights.avgDeposit'), depositDrillFilters())">
            <div class="uim-kpi__label">{{ $t('userInsights.avgDeposit') }}</div>
            <div class="uim-kpi__value">{{ money(m.deposits.avg) }}</div>
            <div class="uim-kpi__foot">{{ $t('userInsights.biggest') }}: {{ money(m.deposits.max) }}</div>
            <i class="fas fa-expand uim-kpi__expand"></i>
          </div>
          <div class="uim-kpi uim-kpi--app">
            <div class="uim-kpi__label">{{ $t('userInsights.approvals') }}</div>
            <div class="uim-kpi__value">{{ m.approvals.count }}</div>
            <div class="uim-kpi__foot">{{ money(m.approvals.total) }}</div>
          </div>
        </div>

        <b-row>
          <!-- ============ LEFT: FTD SHARE / TOP CLIENTS / CALLS ============ -->
          <b-col lg="5" md="12">
            <div class="uim-panel">
              <div class="uim-panel__title"><i class="fas fa-chart-pie mr-50"></i>{{ $t('userInsights.ftdShare') }}</div>
              <div class="uim-split">
                <div class="uim-split__name">{{ $t('userInsights.ftds') }}</div>
                <div class="uim-split__barwrap">
                  <div class="uim-split__bar uim-split__bar--green" :style="{ width: pctWidth(ftdPct) }"></div>
                </div>
                <div class="uim-split__val">{{ money(m.ftd.amount) }} <small>({{ ftdPct }}%)</small></div>
              </div>
              <div class="uim-split">
                <div class="uim-split__name">{{ $t('userInsights.retention') }}</div>
                <div class="uim-split__barwrap">
                  <div class="uim-split__bar uim-split__bar--blue" :style="{ width: pctWidth(retentionPct) }"></div>
                </div>
                <div class="uim-split__val">{{ money(m.retention.amount) }} <small>({{ retentionPct }}%)</small></div>
              </div>
            </div>

            <div class="uim-panel" v-if="topClients.length">
              <div class="uim-panel__title"><i class="fas fa-crown mr-50"></i>{{ $t('userInsights.topClients') }}</div>
              <div v-for="tc in topClients" :key="'tc-' + tc.clientId"
                   class="uim-split uim-split--click" @click="openClient(tc.clientId)"
                   v-b-tooltip :title="$t('clientInsights.openClient')">
                <div class="uim-split__name">
                  {{ tc.clientName || ('#' + tc.clientId) }}
                  <i class="fas fa-arrow-up-right-from-square uim-split__go"></i>
                </div>
                <div class="uim-split__barwrap">
                  <div class="uim-split__bar" :style="{ width: clientBarWidth(tc) }"></div>
                </div>
                <div class="uim-split__val">{{ money(tc.amount) }} <small>({{ tc.count }})</small></div>
              </div>
            </div>

            <div class="uim-panel">
              <div class="uim-panel__title"><i class="fas fa-headset mr-50"></i>{{ $t('userInsights.callsMade') }}</div>
              <div class="uim-mini-stats">
                <div class="uim-mini">
                  <div class="uim-mini__value">{{ calls.total }}</div>
                  <div class="uim-mini__label">
                    <span class="text-success">{{ calls.success }} ✓ {{ $t('userInsights.callSuccess') }}</span>
                    / <span class="text-danger">{{ calls.failed }} ✗</span>
                  </div>
                </div>
                <div class="uim-mini">
                  <div class="uim-mini__value">{{ notes.total || 0 }}</div>
                  <div class="uim-mini__label">{{ $t('userInsights.notesWritten') }}</div>
                </div>
                <div class="uim-mini" v-if="m.deposits.last">
                  <div class="uim-mini__value">{{ m.deposits.last | momentDate }}</div>
                  <div class="uim-mini__label">{{ $t('userInsights.lastDeposit') }}</div>
                </div>
                <div class="uim-mini" v-if="m.deposits.first">
                  <div class="uim-mini__value">{{ m.deposits.first | momentDate }}</div>
                  <div class="uim-mini__label">{{ $t('userInsights.firstDeposit') }}</div>
                </div>
              </div>
            </div>
          </b-col>

          <!-- ============ RIGHT: CHARTS ============ -->
          <b-col lg="7" md="12">
            <div class="uim-panel" v-if="monthly.length">
              <div class="uim-panel__title"><i class="fas fa-chart-column mr-50"></i>{{ $t('userInsights.monthlyActivity') }}</div>
              <apexchart type="line" height="230" :options="monthlyChartOptions" :series="monthlyChartSeries"/>
            </div>

            <div class="uim-panel">
              <div class="uim-panel__title"><i class="fas fa-clock mr-50"></i>{{ $t('userInsights.activityByHour') }}</div>
              <apexchart type="bar" height="200" :options="hourChartOptions" :series="hourChartSeries"/>
            </div>
          </b-col>
        </b-row>
       </b-overlay>
      </div>
    </div>

    <insight-transactions-modal ref="txModal"/>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import InsightTransactionsModal from '@/components/modal/InsightTransactionsModal';
import CalendarBar from '@/components/widget/CalendarBar';

const EMPTY_MONETARY = {
  deposits: { total: 0, count: 0, avg: 0, max: 0, first: null, last: null },
  ftd: { count: 0, amount: 0 },
  retention: { count: 0, amount: 0 },
  withdrawals: { total: 0, count: 0 },
  net: 0,
  monthly: [],
  topClients: [],
  byHour: [],
  approvals: { count: 0, total: 0 },
};
const EMPTY_CALLS = { total: 0, success: 0, failed: 0, lastCallTime: null, byHour: [] };

export default {
  name: 'UserInsightModal',
  mixins: [mixinBase, mixinNotifications],
  components: { InsightTransactionsModal, CalendarBar },
  data: () => ({
    loading: false,
    userId: null,
    rank: null,
    dateRange: { from: null, until: null },
    lastRangeKey: null,
    insight: null,
  }),
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('data', ['getOrganizationNameById']),

    u() { return (this.insight && this.insight.user) || {}; },
    // Deep-guarded monetary block: any missing sub-object falls back to its
    // EMPTY shape so the template never crashes while loading / on partial data.
    m() {
      const raw = (this.insight && this.insight.monetary) || {};
      return {
        deposits: { ...EMPTY_MONETARY.deposits, ...(raw.deposits || {}) },
        ftd: { ...EMPTY_MONETARY.ftd, ...(raw.ftd || {}) },
        retention: { ...EMPTY_MONETARY.retention, ...(raw.retention || {}) },
        withdrawals: { ...EMPTY_MONETARY.withdrawals, ...(raw.withdrawals || {}) },
        net: raw.net != null ? raw.net : 0,
        monthly: raw.monthly || [],
        topClients: raw.topClients || [],
        byHour: raw.byHour || [],
        approvals: { ...EMPTY_MONETARY.approvals, ...(raw.approvals || {}) },
      };
    },
    withdrawals() { return this.m.withdrawals; },
    calls() { return { ...EMPTY_CALLS, ...((this.insight && this.insight.calls) || {}) }; },
    notes() { return (this.insight && this.insight.notes) || { total: 0 }; },

    orgNames() {
      const ids = this.u.orgIds || [];
      return ids
          .map(id => this.getOrganizationNameById(id))
          .filter(n => n && String(n).trim())
          .join(', ');
    },

    ftdPct() {
      const total = Math.abs(this.m.deposits.total || 0);
      return total ? Math.round((Math.abs(this.m.ftd.amount || 0) / total) * 100) : 0;
    },
    retentionPct() {
      const total = Math.abs(this.m.deposits.total || 0);
      return total ? Math.round((Math.abs(this.m.retention.amount || 0) / total) * 100) : 0;
    },

    topClients() { return (this.m.topClients || []).slice(0, 8); },
    monthly() { return this.m.monthly || []; },

    depositHours() { return this.hours24(this.m.byHour); },
    callHours() { return this.hours24(this.calls.byHour); },
    combinedHours() { return this.depositHours.map((v, i) => v + this.callHours[i]); },

    monthlyChartSeries() {
      // FTD is a SUBSET of the deposits, so it renders as a line indicator over
      // the bars instead of a second bar (which read as extra money).
      return [
        { name: this.$t('userInsights.deposits'), type: 'column', data: this.monthly.map(p => Math.round(p.deposits || 0)) },
        { name: this.$t('userInsights.withdrawals'), type: 'column', data: this.monthly.map(p => Math.round(Math.abs(p.withdrawals || 0))) },
        { name: this.$t('userInsights.ftdAmount'), type: 'line', data: this.monthly.map(p => Math.round(p.ftdAmount || 0)) },
      ];
    },
    monthlyChartOptions() {
      return {
        chart: { toolbar: { show: false }, foreColor: this.isDarkSkin ? '#b4b7bd' : '#6e6b7b' },
        colors: ['#28c76f', '#ea5455', '#f6a821'],
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
        stroke: { width: [0, 0, 3], curve: 'smooth' },
        markers: { size: [0, 0, 5], strokeWidth: 2, hover: { sizeOffset: 2 } },
        dataLabels: { enabled: false },
        xaxis: { categories: this.monthly.map(p => p.month) },
        yaxis: { labels: { formatter: v => this.money(v) } },
        legend: { position: 'top' },
        grid: { borderColor: this.isDarkSkin ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
        tooltip: { theme: this.isDarkSkin ? 'dark' : 'light', shared: true, intersect: false },
      };
    },
    hourChartSeries() {
      return [
        { name: this.$t('userInsights.deposits'), data: this.depositHours },
        { name: this.$t('userInsights.callsMade'), data: this.callHours },
      ];
    },
    hourChartOptions() {
      const combined = this.combinedHours;
      const max = Math.max(...combined);
      const best = combined.indexOf(max);
      return {
        chart: { toolbar: { show: false }, stacked: true, foreColor: this.isDarkSkin ? '#b4b7bd' : '#6e6b7b' },
        colors: ['#7367f0', '#00cfe8'],
        plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
        dataLabels: { enabled: false },
        xaxis: { categories: Array.from({ length: 24 }, (_, i) => `${i}h`), tickAmount: 12 },
        legend: { position: 'top' },
        grid: { borderColor: this.isDarkSkin ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
        tooltip: { theme: this.isDarkSkin ? 'dark' : 'light' },
        annotations: max > 0 ? {
          xaxis: [{
            x: `${best}h`,
            borderColor: '#ff9f43',
            label: {
              text: this.$t('userInsights.bestHour'),
              style: { background: '#ff9f43', color: '#fff' },
            },
          }],
        } : {},
      };
    },
  },
  methods: {
    ...mapActions('dashboard', ['getUserInsight']),

    show(userId, rank, range) {
      this.userId = userId;
      this.rank = rank || null;
      this.dateRange = range && range.from
          ? { from: range.from, until: range.until }
          : { from: null, until: null };
      this.insight = null;
      this.$refs.modal.show();
      this.fetchInsight();
    },
    // CalendarBar inside the modal — refetch the stats for the new period.
    // The lastRangeKey guard swallows the bar's initial mount echo.
    onRangeChanged() {
      if (!this.userId || this.loading) return;
      const key = JSON.stringify({ from: this.dateRange.from, until: this.dateRange.until });
      if (key === this.lastRangeKey) return;
      this.fetchInsight();
    },
    fetchInsight() {
      this.loading = true;
      this.lastRangeKey = JSON.stringify({ from: this.dateRange.from, until: this.dateRange.until });
      const range = this.dateRange && this.dateRange.from
          ? { from: this.dateRange.from, until: this.dateRange.until }
          : null;
      this.getUserInsight({ userId: this.userId, range })
          .then(data => { this.insight = data; })
          .catch(err => {
            this.showServerErrorNotification('fetchDataError', 'userInsights', err);
            this.$refs.modal.hide();
          })
          .finally(() => { this.loading = false; });
    },
    onHidden() { this.insight = null; this.userId = null; },
    money(v) { return this.convertToEURO(v || 0); },

    // ---- Stat-card drill-down: open the raw transactions behind a KPI ----
    drill(title, filters) {
      this.$refs.txModal.show(`${this.u.fullName || ''} — ${title}`, filters);
    },
    // Same period as the modal's calendar (empty = lifetime)
    rangeFilter() {
      if (!this.dateRange || !this.dateRange.from) return {};
      return {
        registrationTime: {
          start: this.$moment(this.dateRange.from).startOf('day').toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until || this.dateRange.from).endOf('day').toServerDateTimeFormat(),
        },
      };
    },
    depositDrillFilters() {
      return {
        userIds: [this.userId],
        type: 'DEPOSIT',
        action: ['CREDIT_LINE'],
        status: ['APPROVED'],
        realType: ['REAL'],
        isTest: false,
        ...this.rangeFilter(),
      };
    },
    withdrawDrillFilters() {
      return {
        userIds: [this.userId],
        type: 'WITHDRAWAL',
        status: ['APPROVED'],
        realType: ['REAL'],
        isTest: false,
        ...this.rangeFilter(),
      };
    },
    approvalDrillFilters() {
      return {
        statusUserIds: [this.userId],
        type: 'DEPOSIT',
        action: ['CREDIT_LINE'],
        status: ['APPROVED'],
        realType: ['REAL'],
        isTest: false,
      };
    },
    openClient(clientId) {
      if (clientId) window.open(`/client/${clientId}`, '_blank');
    },
    hours24(list) {
      const out = new Array(24).fill(0);
      (list || []).forEach((v, i) => { if (i < 24) out[i] = v || 0; });
      return out;
    },
    pctWidth(pct) {
      if (!pct || pct <= 0) return '0%';
      return Math.max(4, Math.min(100, pct)) + '%';
    },
    clientBarWidth(row) {
      const max = Math.max(...this.topClients.map(r => Math.abs(r.amount || 0)), 1);
      return Math.max(4, Math.round((Math.abs(row.amount || 0) / max) * 100)) + '%';
    },
  },
};
</script>

<style lang="scss">
.uim-content { border-radius: 18px; overflow: hidden; }
</style>

<style scoped lang="scss">
.uim-loading { padding: 4rem 1rem; text-align: center; color: #9a9dad; }

/* ===== Hero ===== */
.uim-hero {
  display: flex; align-items: center; gap: 1.1rem;
  padding: 1.4rem 1.5rem;
  background: linear-gradient(135deg, #2b2f55 0%, #4b4b9b 55%, #7367f0 100%);
  color: #fff;
  /* Stays visible while the modal body scrolls through long content */
  position: sticky;
  top: 0;
  z-index: 20;
  box-shadow: 0 6px 18px -8px rgba(20, 24, 50, 0.45);

  &__medal { font-size: 2.6rem; line-height: 1; flex-shrink: 0; }
  &__main { flex: 1; min-width: 0; }
  &__name { font-size: 1.4rem; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__sub { font-size: 0.82rem; opacity: 0.88; margin-top: 0.15rem; }
  &__badges { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.6rem; }
  &__actions { display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0; align-self: flex-start; }
  &__close { color: rgba(255, 255, 255, 0.8); &:hover { color: #fff; } }
}
.uim-tag {
  display: inline-flex; align-items: center;
  background: rgba(255, 255, 255, 0.16); backdrop-filter: blur(4px);
  border-radius: 999px; padding: 0.22rem 0.65rem;
  font-size: 0.72rem; font-weight: 600;
}

/* S / R department badges next to the agent name */
.uim-sr {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  font-size: 0.66rem;
  font-weight: 800;
  margin-left: 0.35rem;
  vertical-align: middle;
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);

  &--s { background: linear-gradient(135deg, #34d399, #059669); }
  &--r { background: linear-gradient(135deg, #4da3ff, #0062cc); }
}

/* ===== Body ===== */
.uim-body { padding: 1.25rem 1.5rem 1.5rem; background: #f8f8fb; }
.uim-range { margin-bottom: 1rem; }

/* ===== KPIs ===== */
.uim-kpis {
  display: grid; grid-template-columns: repeat(6, 1fr); gap: 0.75rem; margin-bottom: 1rem;
}
.uim-kpi {
  position: relative;
  border-radius: 14px; padding: 0.85rem 1rem; color: #fff;
  box-shadow: 0 8px 18px -10px rgba(20, 24, 50, 0.35);

  &__label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; opacity: 0.85; }
  &__value { font-size: 1.25rem; font-weight: 800; margin-top: 0.2rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__foot { font-size: 0.72rem; opacity: 0.85; margin-top: 0.15rem; }
  &__expand {
    position: absolute; top: 8px; right: 9px;
    font-size: 0.66rem; opacity: 0;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }

  &--dep { background: linear-gradient(135deg, #34d399, #059669); }
  &--ftd { background: linear-gradient(135deg, #fbbf24, #d97706); }
  &--ret { background: linear-gradient(135deg, #60a5fa, #2563eb); }
  &--wd  { background: linear-gradient(135deg, #fb7185, #dc2626); }
  &--avg { background: linear-gradient(135deg, #a78bfa, #7c3aed); }
  &--app { background: linear-gradient(135deg, #22d3ee, #0891b2); }

  &--click {
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 14px 26px -12px rgba(20, 24, 50, 0.5);
      .uim-kpi__expand { opacity: 0.9; transform: scale(1.15); }
    }
  }
}

/* ===== Panels ===== */
.uim-panel {
  background: #fff; border-radius: 14px; padding: 1rem 1.1rem; margin-bottom: 1rem;
  border: 1px solid rgba(110, 117, 159, 0.1);
  box-shadow: 0 6px 16px -12px rgba(20, 24, 50, 0.18);

  &__title { font-weight: 700; color: #5e5873; font-size: 0.95rem; margin-bottom: 0.75rem; }
}

/* ===== Split bars ===== */
.uim-split {
  display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.4rem;

  &__name { min-width: 120px; max-width: 140px; font-size: 0.78rem; font-weight: 600; color: #5e5873;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__barwrap { flex: 1; height: 8px; border-radius: 999px; background: rgba(110, 117, 159, 0.12); overflow: hidden; }
  &__bar { height: 100%; border-radius: 999px; background: linear-gradient(90deg, #7367f0, #9e95f5); transition: width 0.5s ease; }
  &__bar--green { background: linear-gradient(90deg, #28c76f, #48da8c); }
  &__bar--blue { background: linear-gradient(90deg, #2563eb, #60a5fa); }
  &__val { min-width: 110px; text-align: right; font-size: 0.76rem; font-weight: 700; color: #5e5873; }

  &__go { font-size: 0.6rem; opacity: 0; margin-left: 0.25rem; color: #7367f0; transition: opacity 0.18s ease; }

  &--click {
    cursor: pointer;
    border-radius: 8px;
    margin-left: -0.4rem; margin-right: -0.4rem;
    padding: 0.15rem 0.4rem;
    transition: background 0.15s ease;
    &:hover {
      background: rgba(115, 103, 240, 0.08);
      .uim-split__go { opacity: 1; }
    }
  }
}

/* ===== Mini stats ===== */
.uim-mini-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.7rem; }
.uim-mini {
  background: rgba(115, 103, 240, 0.06); border-radius: 12px; padding: 0.7rem 0.8rem; text-align: center;

  &__value { font-weight: 800; color: #5e5873; font-size: 1rem; }
  &__label { font-size: 0.7rem; color: #9a9dad; margin-top: 0.1rem; }
}

/* ===== Dark mode ===== */
.uim--dark {
  .uim-body { background: #161d31; }
  .uim-panel { background: #283046; border-color: rgba(255, 255, 255, 0.06);
    &__title { color: #d0d2d6; }
  }
  .uim-split__name, .uim-split__val, .uim-mini__value { color: #d0d2d6; }
  .uim-mini { background: rgba(115, 103, 240, 0.12); }
  .uim-split__barwrap { background: rgba(255, 255, 255, 0.08); }
}

@media (max-width: 1400px) { .uim-kpis { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 768px) {
  .uim-kpis { grid-template-columns: repeat(2, 1fr); }
  .uim-hero { flex-wrap: wrap; }
}
</style>
